import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "overflow-x-auto" }
const _hoisted_2 = { class: "w-full border-separate border-spacing-0 text-sm" }
const _hoisted_3 = { class: "sticky top-0 z-10 bg-blue-500 dark:bg-blue-600" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "flex items-center gap-1" }
const _hoisted_6 = {
  key: 0,
  class: "opacity-60"
}
const _hoisted_7 = ["onMousedown"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium" }
const _hoisted_10 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300" }
const _hoisted_11 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-mono text-xs" }
const _hoisted_12 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-mono text-xs" }
const _hoisted_13 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300" }
const _hoisted_14 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300" }
const _hoisted_15 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700" }
const _hoisted_16 = { class: "flex flex-wrap gap-1" }
const _hoisted_17 = {
  key: 0,
  class: "inline-block px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
}
const _hoisted_18 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_19 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300" }
const _hoisted_20 = { class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700" }
const _hoisted_21 = { class: "relative" }
const _hoisted_22 = ["onClick"]
const _hoisted_23 = {
  key: 0,
  class: "absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[150px] z-20 overflow-hidden"
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = ["onClick"]

import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { taskSentinelService } from '../../../services/task-sentinel'
import type { Team, SortConfig, Manager } from '../../../types/task-sentinel'


export default /*@__PURE__*/_defineComponent({
  __name: 'TeamsTable',
  props: {
    teams: {},
    sortConfig: {}
  },
  emits: ["sort", "view", "duplicate"],
  setup(__props: any, { emit: __emit }) {

const theme = inject('theme', useTheme())

const props = __props

const emit = __emit

// Column definitions
const columns = [
  { key: 'name', label: 'Team', sortable: true, resizable: true },
  { key: 'director', label: 'Director', sortable: true, resizable: true },
  { key: 'standupId', label: 'Folder ID', sortable: true, resizable: true },
  { key: 'roadmap', label: 'Roadmap', sortable: true, resizable: true },
  { key: 'em', label: 'EM Email', sortable: true, resizable: true },
  { key: 'tl', label: 'TL Email', sortable: true, resizable: true },
  { key: 'devs', label: 'Developer Emails', sortable: true, resizable: true },
  { key: 'pm', label: 'PM List', sortable: true, resizable: true },
  { key: 'deleted', label: 'Is Paused?', sortable: true, resizable: true },
  { key: 'actions', label: 'Actions', sortable: false, resizable: false }
]

// State
const openMenuId = ref<string | null>(null)
const columnWidths = ref<Record<number, number>>({})
const isResizing = ref(false)
const resizingColumn = ref<number | null>(null)
const startX = ref(0)
const startWidth = ref(0)

// Load saved column widths
onMounted(() => {
  const saved = localStorage.getItem('taskSentinel_columnWidths')
  if (saved) {
    try {
      columnWidths.value = JSON.parse(saved)
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Close menu on outside click
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
})

// Computed
const sortedTeams = computed(() => {
  if (props.sortConfig.column < 0) return props.teams

  const sorted = [...props.teams].sort((a, b) => {
    let aVal: string
    let bVal: string

    switch (props.sortConfig.column) {
      case 0:
        aVal = (a.name || '').toLowerCase()
        bVal = (b.name || '').toLowerCase()
        break
      case 1:
        aVal = getManagerEmails(a.managers, 'DIRECTOR').toLowerCase()
        bVal = getManagerEmails(b.managers, 'DIRECTOR').toLowerCase()
        break
      case 2:
        aVal = (a.standupId || '').toLowerCase()
        bVal = (b.standupId || '').toLowerCase()
        break
      case 3:
        aVal = (a.roadmapBoardId || '').toLowerCase()
        bVal = (b.roadmapBoardId || '').toLowerCase()
        break
      case 4:
        aVal = getManagerEmails(a.managers, 'EM').toLowerCase()
        bVal = getManagerEmails(b.managers, 'EM').toLowerCase()
        break
      case 5:
        aVal = getManagerEmails(a.managers, 'TL').toLowerCase()
        bVal = getManagerEmails(b.managers, 'TL').toLowerCase()
        break
      case 6:
        aVal = taskSentinelService.getDeveloperEmails(a.devs).toLowerCase()
        bVal = taskSentinelService.getDeveloperEmails(b.devs).toLowerCase()
        break
      case 7:
        aVal = getPMList(a.managers).toLowerCase()
        bVal = getPMList(b.managers).toLowerCase()
        break
      case 8:
        aVal = a.deleted ? 'yes' : 'no'
        bVal = b.deleted ? 'yes' : 'no'
        break
      default:
        return 0
    }

    if (aVal < bVal) return props.sortConfig.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return props.sortConfig.direction === 'asc' ? 1 : -1
    return 0
  })

  return sorted
})

// Methods
const getManagerEmails = (managers: Manager[] | undefined, type: string): string => {
  return taskSentinelService.getManagerEmails(managers, type)
}

const getPMList = (managers: Manager[] | undefined): string => {
  return taskSentinelService.getPMList(managers)
}

const toggleMenu = (teamId: string) => {
  openMenuId.value = openMenuId.value === teamId ? null : teamId
}

const handleOutsideClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    openMenuId.value = null
  }
}

const handleEdit = (team: Team) => {
  openMenuId.value = null
  emit('view', team)
}

const handleDuplicate = (team: Team) => {
  openMenuId.value = null
  emit('duplicate', team)
}

// Column resizing
const startResize = (e: MouseEvent, columnIndex: number) => {
  isResizing.value = true
  resizingColumn.value = columnIndex
  startX.value = e.pageX
  startWidth.value = columnWidths.value[columnIndex] || 150

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

const handleResizeMove = (e: MouseEvent) => {
  if (!isResizing.value || resizingColumn.value === null) return

  const diff = e.pageX - startX.value
  const newWidth = Math.max(50, startWidth.value + diff)
  columnWidths.value[resizingColumn.value] = newWidth
}

const handleResizeEnd = () => {
  isResizing.value = false
  resizingColumn.value = null

  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''

  // Save column widths
  localStorage.setItem('taskSentinel_columnWidths', JSON.stringify(columnWidths.value))
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'border rounded-lg overflow-hidden'
    ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("table", _hoisted_2, [
        _createElementVNode("thead", _hoisted_3, [
          _createElementVNode("tr", null, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(columns, (column, index) => {
              return _createElementVNode("th", {
                key: column.key,
                class: _normalizeClass([
                'px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide cursor-pointer select-none hover:bg-blue-600 dark:hover:bg-blue-700 transition-all relative',
                column.sortable ? 'cursor-pointer' : ''
              ]),
                style: _normalizeStyle(
                columnWidths.value[index]
                  ? { width: `${columnWidths.value[index]}px`, minWidth: `${columnWidths.value[index]}px` }
                  : {}
              ),
                onClick: ($event: any) => (column.sortable && _ctx.$emit('sort', index))
              }, [
                _createElementVNode("span", _hoisted_5, [
                  _createTextVNode(_toDisplayString(column.label) + " ", 1),
                  (column.sortable)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_ctx.sortConfig.column === index
                      ? _ctx.sortConfig.direction === 'asc'
                        ? '↑'
                        : '↓'
                      : '↕'), 1))
                    : _createCommentVNode("", true)
                ]),
                (column.resizable !== false)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-white/30 active:bg-white/50",
                      onMousedown: _withModifiers(($event: any) => (startResize($event, index)), ["stop"])
                    }, null, 40, _hoisted_7))
                  : _createCommentVNode("", true)
              ], 14, _hoisted_4)
            }), 64))
          ])
        ]),
        _createElementVNode("tbody", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedTeams.value, (team) => {
            return (_openBlock(), _createElementBlock("tr", {
              key: team.id,
              class: _normalizeClass([
              'border-l-2 border-l-transparent transition-all cursor-pointer',
              _unref(theme).isDark.value
                ? 'hover:bg-gray-700 hover:border-l-blue-500'
                : 'hover:bg-blue-50 hover:border-l-blue-500'
            ]),
              onClick: ($event: any) => (_ctx.$emit('view', team))
            }, [
              _createElementVNode("td", _hoisted_9, _toDisplayString(team.name || '-'), 1),
              _createElementVNode("td", _hoisted_10, _toDisplayString(getManagerEmails(team.managers, 'DIRECTOR')), 1),
              _createElementVNode("td", _hoisted_11, _toDisplayString(team.standupId || '-'), 1),
              _createElementVNode("td", _hoisted_12, _toDisplayString(team.roadmapBoardId || '-'), 1),
              _createElementVNode("td", _hoisted_13, _toDisplayString(getManagerEmails(team.managers, 'EM')), 1),
              _createElementVNode("td", _hoisted_14, _toDisplayString(getManagerEmails(team.managers, 'TL')), 1),
              _createElementVNode("td", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((team.devs || []).slice(0, 4), (dev) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: dev.emailPrefix,
                      class: "inline-block px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                    }, _toDisplayString(dev.emailPrefix), 1))
                  }), 128)),
                  ((team.devs || []).length > 4)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, " +" + _toDisplayString(team.devs.length - 4) + " more ", 1))
                    : _createCommentVNode("", true),
                  (!team.devs || team.devs.length === 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_18, "-"))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("td", _hoisted_19, _toDisplayString(getPMList(team.managers)), 1),
              _createElementVNode("td", _hoisted_20, [
                _createElementVNode("span", {
                  class: _normalizeClass([
                  'inline-block px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wide border',
                  team.deleted
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
                    : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
                ])
                }, _toDisplayString(team.deleted ? 'Yes' : 'No'), 3)
              ]),
              _createElementVNode("td", {
                class: "px-4 py-3 border-b border-gray-200 dark:border-gray-700",
                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("button", {
                    class: "p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300",
                    onClick: ($event: any) => (toggleMenu(team.id))
                  }, " ⋯ ", 8, _hoisted_22),
                  (openMenuId.value === team.id)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        _createElementVNode("button", {
                          class: "w-full px-4 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center gap-2",
                          onClick: ($event: any) => (handleEdit(team))
                        }, _cache[1] || (_cache[1] = [
                          _createElementVNode("span", null, "✏️", -1),
                          _createElementVNode("span", null, "Edit", -1)
                        ]), 8, _hoisted_24),
                        _createElementVNode("button", {
                          class: "w-full px-4 py-2 text-left text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2",
                          onClick: ($event: any) => (handleDuplicate(team))
                        }, _cache[2] || (_cache[2] = [
                          _createElementVNode("span", null, "📋", -1),
                          _createElementVNode("span", null, "Duplicate", -1)
                        ]), 8, _hoisted_25)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ], 10, _hoisted_8))
          }), 128))
        ])
      ])
    ])
  ], 2))
}
}

})